import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { ChefHat, Package, Users, AlertCircle } from 'lucide-react';

const MealCountsPanel = ({ mealCounts, servableCustomerCount, customersWithoutPreferences }) => {
  if (!mealCounts) return null;

  const { breakfast, lunch, dinner, totalMeals, totalVeg, totalNonVeg } = mealCounts;

  return (
    <div className="space-y-4">
      {/* Warning for customers without preferences */}
      {customersWithoutPreferences > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {customersWithoutPreferences} active customer(s) have no preference data and are excluded from meal counts.
          </AlertDescription>
        </Alert>
      )}

      {/* Top summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-2 border-orange-400 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white">Total Meals</CardTitle>
            <Package className="h-5 w-5 text-white" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{totalMeals}</div>
            <p className="text-xs text-orange-100">Meals to prepare today</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-2 border-green-500 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900">Veg Meals</CardTitle>
            <ChefHat className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{totalVeg}</div>
            <p className="text-xs text-gray-600">Vegetarian meals</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-2 border-red-400 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900">Non-Veg Meals</CardTitle>
            <ChefHat className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">{totalNonVeg}</div>
            <p className="text-xs text-gray-600">Non-vegetarian meals</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-2 border-blue-500 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-900">Active Customers</CardTitle>
            <Users className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">{servableCustomerCount}</div>
            <p className="text-xs text-gray-600">Servable today</p>
          </CardContent>
        </Card>
      </div>

      {/* Per-meal breakdown */}
      <div className="grid gap-4 md:grid-cols-3">
        {[
          { label: 'Breakfast', data: breakfast, icon: '🌅' },
          { label: 'Lunch', data: lunch, icon: '☀️' },
          { label: 'Dinner', data: dinner, icon: '🌙' },
        ].map(({ label, data, icon }) => (
          <Card key={label} className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <span>{icon}</span> {label}
                <span className="ml-auto text-2xl font-bold text-orange-500">{data.total}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                  <span className="text-gray-600">Veg</span>
                  <span className="font-bold text-green-600">{data.veg}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
                  <span className="text-gray-600">Non-Veg</span>
                  <span className="font-bold text-red-500">{data.nonVeg}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MealCountsPanel;
